<template lang="pug">
  div
    div.cancel-signin-message(v-if='userCancelledSignIn')
      .loader
        .loader-spinner
        h2.h2.message(v-t='"cancelSignInText"')
    ModalPopup.auth-popup-page(modal-id='authentication')
      .modal-content(:class='{ pwl: displayPasswordlessUI }')
        button.close.auth-close(
          :class='{ "auth-close-pwl": displayPasswordlessUI }'
          type='button' data-dismiss='modal' aria-label='Close' @click='cancelSignIn'
        )
        .modal-body(:class='{ pwl: displayPasswordlessUI }')
          Authentication()
</template>

<i18n>
{
  "lng_en": {
    "alreadyAuthenticated": "You are already signed in.",
    "cancelSignInText": "Bringing you back to home page..."
  },
  "lng_id": {
    "alreadyAuthenticated": "Andah sudah masuk.",
    "cancelSignInText": "Membawa Anda kembali ke halaman sebelumnya..."
  },
  "lng_th": {
    "alreadyAuthenticated": "คุณเข้าสู่ระบบอยู่แล้ว",
    "cancelSignInText": "กำลังพาคุณกลับไปหน้าที่แล้ว..."
  },
  "lng_zh": {
    "alreadyAuthenticated": "您已经登入。",
    "cancelSignInText": "正在返回前一頁..."
  },
  "lng_ko": {
    "alreadyAuthenticated": "이미 로그인되어 있습니다.",
    "cancelSignInText": "이전 페이지로 이동합니다..."
  }
}
</i18n>

<script>
import { mapMutations, mapState } from 'vuex'
import ModalPopup from '@/components/shared/modal-popup.vue'
import Loader from '@/components/shared/loader.vue'
import { sanitizeUrl } from '@sephora-asia/sanitize-url'
import { isUrlSanitizationEnabled } from '@/modules/services/utilities'
import { authPopupMixins } from '@/mixins/gtmMixins'
import { authenticationSteps } from '@/modules/config/authentication'

const HOME_PAGE = '/'
const MODAL_ID = '#authentication'

export default {
  components: {
    ModalPopup,
    Authentication: () => ({
      component: import(/* webpackChunkName: 'vue-authentication' */ './main.vue'),
      loading: Loader,
      delay: 100
    })
  },

  mixins: [
    authPopupMixins
  ],

  data () {
    return { userCancelledSignIn: false }
  },

  computed: {
    ...mapState({
      currentUser: state => state.user.currentUser,
      authStep: state => state.authentication.authStep,
      isEnablePasswordlessSignIn: () => vueSupport?.featureFlags?.enablePasswordlessSignIn
    }),

    returnTo () {
      return this.$router.currentRoute.query['return-to'] || this.$router.currentRoute.query.return_to || HOME_PAGE
    },

    displayPasswordlessUI () {
      return this.isEnablePasswordlessSignIn && (
        [
          authenticationSteps.VALIDATE,
          authenticationSteps.SELECT_SIGN_IN_METHOD,
          authenticationSteps.SIGN_IN_OTP,
          authenticationSteps.AUTH_FRAUD_CHECK_OTP,
          authenticationSteps.PASSWORD_SIGN_IN,
          authenticationSteps.SIGN_UP_FORM,
          authenticationSteps.LATEST_SERVICE_AGREEMENT
        ].includes(this.authStep)
      )
    }
  },

  async mounted () {
    if (this.currentUser) {
      this.setNotification({
        msg: this.$t('alreadyAuthenticated')
      })

      const returnPath = await isUrlSanitizationEnabled() ? sanitizeUrl(this.returnTo, HOME_PAGE) : this.returnTo
      window.location.replace(returnPath)
    } else if (this.$router.currentRoute.query.msg) {
      this.setNotification({
        msg: this.$router.currentRoute.query.msg
      })
    }

    this.setupDefaultReturnPath()

    this.openAuth()
    this.resetAlreadyTracked()

    // This ensures that closing the modal redirects to homepage
    // doc: https://getbootstrap.com/docs/4.0/components/modal/#events
    $(MODAL_ID).on('hide.bs.modal', this.cancelSignIn)
  },

  destroyed () {
    this.resetAlreadyTracked()
    $(MODAL_ID).off('hide.bs.modal', this.cancelSignIn)
  },

  methods: {
    ...mapMutations([
      'setNotification'
    ]),

    openAuth () {
      $(MODAL_ID).modal('show')
      this.$store.commit('setShowAuthPopup', true)
    },

    async cancelSignIn () {
      this.userCancelledSignIn = true
      if (this.authStep === authenticationSteps.LATEST_SERVICE_AGREEMENT) {
        await this.$store.dispatch('signOut')
        this.$store.commit('setCurrentUser', false)
      }
      if (this.clickValue() && !this.alreadyTracked) {
        this.trackCloseAuthenticationModal()
      }
      this.alreadyTracked = true
      // return to homepage if the authentication process is cancelled
      window.location.replace(HOME_PAGE)
    },

    setupDefaultReturnPath () {
      const returnTo = this.$router.currentRoute.query['return-to'] || this.$router.currentRoute.query.return_to
      if (!returnTo) {
        // This will be used by the post sign in function in the authentication workflow to redirect user to this url
        this.$router.replace({ query: { return_to: HOME_PAGE, msg: this.$router.currentRoute.query.msg } })
      }
    }
  }
}
</script>

<style lang='scss'>
  .auth-popup-page .modal-dialog {
    max-width: 500px;

    @include media-breakpoint-down(sm) {
      margin: 0;
      min-height: inherit;
    }
  }
</style>

<style lang='scss' scoped>
  #authentication .authentication {
    max-width: none;
    margin: 23px 25px;

    @include media-breakpoint-up(sm) {
      margin: 40px 30px 15px;
    }
  }

  .auth-close {
    position: absolute;
    right: 25px;
    top: 20px;
    z-index: $zindex-fixed;

    @include media-breakpoint-up(sm) {
      right: 50px;
      top: 40px;
    }
  }

  .auth-close-pwl {
    position: absolute;
    right: 25px;
    top: 35px;
    z-index: $zindex-fixed;
    color: $black;

    @include media-breakpoint-down(sm) {
      top: 36px;
    }
  }

  .modal-body {
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 25px;

    @include media-breakpoint-down(sm) {
      padding: 0;
    }

    &.pwl {
      padding: inherit;
    }

    .text {
      text-align: left;
      font-size: 14px;

      p {
        padding-bottom: 10px;
      }
    }
  }

  .modal-content {
    @include media-breakpoint-down(sm) {
      height: 100%;
      min-height: 100vh;
    }

    &.pwl {
      border-radius: 8px;
      width: fit-content;
      min-width: 343px;

      @include media-breakpoint-down(sm) {
        width: auto;
      }

      @include media-breakpoint-up(md) {
        margin: auto;
      }
    }
  }

  .cancel-signin-message {
    .loader {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 400px;

      .loader-spinner {
        width: 60px;
        height: 60px;
      }
    }

    .message {
      &:first-letter {
        text-transform: uppercase;
      }

      margin: 40px 30px;
      text-align: center;
    }
  }
</style>
